import { ReturnOpportunityReadable } from '@crm/api'
import { ActionOpportunityData } from '@crm/features/opportunities/types'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { paths } from '@crm/routes'
import { composePrice } from '@crm/utils'
import { ConditionalText, DayLabel } from '@shared/components'
import { useLocales } from '@shared/locales'
import { getFullName } from '@shared/utils'
import { Button, Card, CardProps, Col, Flex, Row, theme, Tooltip } from 'antd'
import {
  DollarCircle,
  Eye,
  Maximize4,
  Messages3,
  MoneyChange,
  Personalcard,
  TaskSquare,
  User
} from 'iconsax-react'
import { PointerEvent } from 'react'

import { useStyles } from './styles'

type Props = {
  data?: ReturnOpportunityReadable
  onActionClick?: (actionData: ActionOpportunityData) => void
} & CardProps

export const OpportunityKanbanCard = ({
  data,
  onActionClick,
  ...props
}: Props) => {
  const { t } = useLocales()
  const { styles } = useStyles()

  const amount = composePrice(data?.value, data?.currency?.symbol)

  const { token } = theme.useToken()

  const navigate = useAppNavigate()

  const onClickPreview = () =>
    navigate(
      paths.opportunitiesDetails.root(String(data?.id), String(data?.lead?.id))
    )

  const onClickTasks = () =>
    navigate(
      paths.opportunitiesDetails.tasks(String(data?.id), String(data?.lead?.id))
    )

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) =>
    event.stopPropagation()

  return (
    <Card className={styles.card} {...props}>
      <Flex vertical gap={4} className={styles.cardContent}>
        <Row align="middle" gutter={4}>
          <Col span={21}>
            <Tooltip
              title={t('contacts.opportunity')}
              trigger="hover"
              placement="left"
            >
              <Flex align="center" gap={8}>
                <Flex>
                  <MoneyChange size={16} color={token.colorIcon} />
                </Flex>

                <ConditionalText
                  condition={!!data?.name}
                  isEllipsis
                  weight={500}
                >
                  {data?.name}
                </ConditionalText>
              </Flex>
            </Tooltip>
          </Col>
          <Col span={3}>
            <Tooltip title={t('edit_opportunity')} trigger="hover">
              <Button
                onClick={(e) => {
                  e.stopPropagation()
                  onActionClick?.({
                    open: true,
                    type: 'edit',
                    opportunityId: String(data?.id)
                  })
                }}
                className={styles.expandButton}
                icon={<Maximize4 size={16} color={token.colorIcon} />}
                type="text"
                size="small"
              />
            </Tooltip>
          </Col>
        </Row>

        <Col span={21}>
          <Tooltip title={t('contacts.lead')} trigger="hover" placement="left">
            <Flex align="center" gap={8}>
              <Flex>
                <Personalcard size={16} color={token.colorIcon} />
              </Flex>

              <ConditionalText
                condition={!!data?.lead?.name}
                isEllipsis
                weight={500}
              >
                {data?.lead?.name}
              </ConditionalText>
            </Flex>
          </Tooltip>
        </Col>

        <Tooltip title={t('lead.value')} trigger="hover" placement="left">
          <Flex align="center" gap={8}>
            <DollarCircle size={16} color={token.colorIcon} />

            <ConditionalText condition={!!amount} isEllipsis weight={500}>
              {amount}
            </ConditionalText>
          </Flex>
        </Tooltip>

        <Tooltip title={t('task.owner')} trigger="hover" placement="left">
          <Flex align="center" gap={8}>
            <Flex>
              <User size={16} color={token.colorIcon} />
            </Flex>

            <ConditionalText
              condition={!!getFullName(data?.owner)}
              isEllipsis
              weight={500}
            >
              {getFullName(data?.owner)}
            </ConditionalText>
          </Flex>
        </Tooltip>
      </Flex>

      <Flex
        justify="space-between"
        align="center"
        gap={8}
        onPointerDown={onPointerDown}
      >
        <Flex align="center">
          <Tooltip title={t('preview')} trigger="hover">
            <Button
              icon={<Eye size={16} />}
              type="text"
              size="small"
              onClick={onClickPreview}
            />
          </Tooltip>

          <Tooltip title={t('tasks')} trigger="hover">
            <Button
              icon={<TaskSquare size={16} />}
              type="text"
              size="small"
              onClick={onClickTasks}
            />
          </Tooltip>

          <Tooltip title={t('contacts.comment')} trigger="hover">
            <Button
              icon={<Messages3 size={16} />}
              type="text"
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                onActionClick?.({
                  open: true,
                  type: 'comments',
                  opportunityId: String(data?.id)
                })
              }}
            />
          </Tooltip>
        </Flex>

        <Tooltip title={t('modified_at')} trigger="hover">
          <DayLabel date={data?.created_at} />
        </Tooltip>
      </Flex>
    </Card>
  )
}
